import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useCallback,
} from 'react';

import { post } from 'core/fetch';
import PropTypes from 'prop-types';

import reducer, {
  initialState,
  IAuthState,
  AuthActionTypes,
} from './state/AuthState';

interface AuthContextType {
  state: IAuthState;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  state: initialState,
  logout: () => Promise.reject(),
});

const useAuthContext = () => useContext(AuthContext);

const AuthContextProvider = ({ children }: React.PropsWithChildren<any>) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  /*
   * ACTIONS
   */
  const logout = useCallback(async () => {
    dispatch({ type: AuthActionTypes.Logout });

    try {
      await post(`/auth/logout`, {});
      dispatch({ type: AuthActionTypes.LogoutSuccess });
    } catch (_e) {
      dispatch({ type: AuthActionTypes.LogoutFail });
    }
  }, []);

  useEffect(() => {
    const fetchSession = async () => {
      dispatch({ type: AuthActionTypes.FETCH_SESSION_REQUEST });

      fetch(`/api/auth-session`, {
        method: 'GET',
        cache: 'no-store',
      })
        .then(async res => {
          const responseJson = await res.json();
          dispatch({
            type: AuthActionTypes.FETCH_SESSION_SUCCESS,
            payload: responseJson?.user,
          });
        })
        .catch(e =>
          dispatch({ type: AuthActionTypes.FETCH_SESSION_FAIL, payload: e }),
        );
    };
    fetchSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export { useAuthContext };
export default AuthContextProvider;
